// resolveInlineLink — parse a link the agent wrote in markdown into an
// `InlineLinkTarget`. Parse-don't-validate at the boundary, total, pure.
//
// The agent links resources as ordinary links to their CLIENT route (the URL is
// the client's public API — no bespoke link syntax): `/library/{id}` (canonical),
// and the other resource routes `parseRoute` already knows (`/knowledge/{id}`,
// `/source/{id}`, `/material/{id}`, `/package/{id}`). The id must be in the library index
// (`refFor`) — the model can only link what a tool result gave it, and a stale
// or invented id degrades to plain text, never a dead link. http(s) URLs are
// external. Everything else (other schemes, non-resource routes, relative
// paths) is plain text.

export { resolveInlineLink };

import { exhaustSwitchCase } from "@bilda/utils";

import type {
    InlineLinkTarget,
    ResolveInlineLink,
} from "../../components/Chat/InlineLink/InlineLink.js";
import type {
    ResourceId,
    ResourceRef,
} from "../../components/Navigation/types.js";
import { parseRoute } from "./parse.js";
import type { Route } from "./types.js";

// Any origin works: only the path of an in-app href is read.
const IN_APP_BASE = "https://app.invalid";

const resourceIdOf = (route: Route): ResourceId | undefined => {
    switch (route.type) {
        case "library":
        case "knowledge":
            return route.selected;
        case "package":
            return route.id;
        case "chat":
        case "journal":
        case "studio":
        case "classicStudio":
        case "projects":
        case "conversation":
        case "quality":
        case "settings":
        case "notFound":
            return undefined;
        default:
            return exhaustSwitchCase(route);
    }
};

const unsupported: InlineLinkTarget = {
    type: "plain",
    reason: "unsupported_href",
};

const resolveInlineLink =
    (refFor: (id: ResourceId) => ResourceRef | undefined): ResolveInlineLink =>
    (href) => {
        if (/^https?:\/\//i.test(href)) return { type: "external", href };
        // An in-app absolute path; `//host` is protocol-relative, not in-app.
        if (!href.startsWith("/") || href.startsWith("//")) return unsupported;
        const id = resourceIdOf(parseRoute(new URL(href, IN_APP_BASE)));
        if (id === undefined) return unsupported;
        const ref = refFor(id);
        return ref === undefined
            ? { type: "plain", reason: "unknown_resource" }
            : { type: "resource", ref };
    };
